import { Checkbox } from '@artstorefronts/ui';
import { Trans, useTranslation } from 'react-i18next';

const TERMS_URL = 'https://www.arthelper.ai/legal/terms-of-service';
const PRIVACY_URL = 'https://www.arthelper.ai/legal/privacy-policy';

export interface SmsConsentCheckboxesProps {
  /** Prefix for checkbox DOM ids; must be unique on the page (e.g. 'sms-consent', 'phone-number-consent'). */
  idPrefix: string;
  smsConsent: boolean;
  onSmsConsentChange: (checked: boolean) => void;
  smsConsentError?: string;
  termsAccepted: boolean;
  onTermsAcceptedChange: (checked: boolean) => void;
  termsAcceptedError?: string;
  disabled?: boolean;
}

/** Shared TCPA consent checkboxes (SMS opt-in + terms/privacy) used by every phone-capture entry point. */
export function SmsConsentCheckboxes({
  idPrefix,
  smsConsent,
  onSmsConsentChange,
  smsConsentError,
  termsAccepted,
  onTermsAcceptedChange,
  termsAcceptedError,
  disabled,
}: SmsConsentCheckboxesProps) {
  const { t } = useTranslation('common');
  const smsConsentId = `${idPrefix}-sms-consent`;
  const termsAcceptedId = `${idPrefix}-terms-accepted`;

  return (
    <div className="space-y-2">
      <label htmlFor={smsConsentId} className="flex items-start gap-2 text-xs text-gray-600 cursor-pointer">
        <Checkbox
          id={smsConsentId}
          className="mt-0.5 w-4 h-4 shrink-0"
          checked={smsConsent}
          onCheckedChange={(checked) => onSmsConsentChange(checked === true)}
          disabled={disabled}
        />
        <span>{t('sms-tips.sms-consent')}</span>
      </label>
      {smsConsentError && <p className="text-red-600 text-sm">{smsConsentError}</p>}

      <label htmlFor={termsAcceptedId} className="flex items-start gap-2 text-xs text-gray-600 cursor-pointer">
        <Checkbox
          id={termsAcceptedId}
          className="mt-0.5 w-4 h-4 shrink-0"
          checked={termsAccepted}
          onCheckedChange={(checked) => onTermsAcceptedChange(checked === true)}
          disabled={disabled}
        />
        <span>
          <Trans
            i18nKey="sms-tips.terms-privacy-consent"
            t={t}
            components={{
              terms: (
                <a
                  href={TERMS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline"
                  onClick={(e) => e.stopPropagation()}
                />
              ),
              privacy: (
                <a
                  href={PRIVACY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline"
                  onClick={(e) => e.stopPropagation()}
                />
              ),
            }}
          />
        </span>
      </label>
      {termsAcceptedError && <p className="text-red-600 text-sm">{termsAcceptedError}</p>}
    </div>
  );
}
